import { ALL_TOOLS, type AuditRecord } from '@lastrouter/contracts';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Field,
  Input,
  NativeSelect,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@lastrouter/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { cn, Download, ShieldCheck } from '@lastrouter/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, downloadUrl } from '../api/client.js';
import { useApiQuery } from '../api/hooks.js';
import { AdminOnly } from '../app/shell.js';
import { EmptyState, ErrorState, KeyValue, LoadingBlock, PageHeader, StatusBadge } from '../components/common.js';
import { formatDateTime, formatNumber } from '../lib/format.js';

type Filters = { from: string; to: string; user: string; tool: string; status: string; subject_id: string };
const EMPTY: Filters = { from: '', to: '', user: '', tool: '', status: '', subject_id: '' };
const STATUS_TONE = { ok: 'success', clarification: 'warning', no_answer: 'neutral', error: 'danger' } as const;

/** Datas do filtro (AAAA-MM-DD) viram o início e o fim do dia em ISO. */
function toQuery(f: Filters) {
  return {
    ...(f.from ? { from: `${f.from}T00:00:00.000Z` } : {}),
    ...(f.to ? { to: `${f.to}T23:59:59.999Z` } : {}),
    ...(f.user ? { user: f.user } : {}),
    ...(f.tool ? { tool: f.tool as AuditRecord['tool'] & string } : {}),
    ...(f.status ? { status: f.status as AuditRecord['status'] } : {}),
    ...(f.subject_id ? { subject_id: f.subject_id } : {}),
  };
}

function Detail({ r, onClose }: { r: AuditRecord; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('audit.detail')} description={r.correlation_id} closeLabel={t('common.close')} className="w-[min(760px,calc(100vw-32px))]">
        <KeyValue
          items={[
            { label: t('audit.when'), value: formatDateTime(r.ts) },
            { label: t('audit.user'), value: r.user },
            { label: t('audit.channel'), value: `${r.channel}${r.client_app ? ` (${r.client_app})` : ''}` },
            { label: t('audit.tool'), value: r.tool ?? t('audit.beforeTool') },
            { label: t('audit.status'), value: `${t(`audit.statuses.${r.status}`)}${r.error_code ? ` (${r.error_code})` : ''}` },
            { label: t('audit.subject'), value: r.subject_id ?? '-' },
            { label: t('audit.query'), value: r.query_id ? `${r.query_id} v${r.query_version}` : '-' },
            { label: t('audit.expressions'), value: r.expressions.length ? <code className="block whitespace-pre-wrap break-all font-mono text-[length:var(--lr-text-xs)]">{r.expressions.join('\n')}</code> : '-' },
            { label: t('audit.publication'), value: r.publication_id ?? '-' },
            { label: t('audit.promptModel'), value: r.prompt_version ? `${r.prompt_version} / ${r.model_id}` : '-' },
            { label: t('audit.versions'), value: `lastrouter ${r.lastrouter_version}${r.adapter_version ? `, ${r.adapter_version}` : ''}` },
            { label: t('audit.lastReload'), value: formatDateTime(r.source_last_reload_at) },
            { label: t('audit.rows'), value: r.rows ?? '-' },
            { label: t('audit.duration'), value: `${formatNumber(r.duration_ms)} ms` },
            { label: t('audit.tokens'), value: r.tokens ? `${r.tokens.input} / ${r.tokens.output}` : '-' },
            { label: t('audit.result'), value: r.result_id ?? '-' },
            { label: t('audit.hash'), value: r.hash ? <code className="break-all font-mono text-[length:var(--lr-text-xs)]">{r.hash}</code> : '-' },
          ]}
        />
      </DialogContent>
    </Dialog>
  );
}

export function AuditPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters>(EMPTY);
  const [detail, setDetail] = useState<AuditRecord | null>(null);
  const subjects = useApiQuery('subjects');
  const verify = useApiQuery('auditVerify', undefined, { enabled: false });

  const q = useInfiniteQuery({
    queryKey: ['audit', applied],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api('audit', { query: { ...toQuery(applied), limit: 50, ...(pageParam ? { cursor: pageParam } : {}) } }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];

  function submit(e: FormEvent) {
    e.preventDefault();
    setApplied(form);
  }

  const chain = verify.data;
  return (
    <AdminOnly>
      <PageHeader
        title={t('audit.title')}
        subtitle={t('audit.subtitle')}
        actions={
          <>
            <Button onClick={() => void verify.refetch()} disabled={verify.isFetching}>
              <ShieldCheck />
              {t('audit.verify')}
            </Button>
            <Button asChild>
              <a href={downloadUrl('auditExport', toQuery(applied))} download="auditoria.csv">
                <Download />
                {t('audit.export')}
              </a>
            </Button>
          </>
        }
      />

      {chain ? (
        <div className="mb-4" aria-live="polite">
          {chain.ok ? (
            <Alert tone="success" title={t('audit.chainOk', { records: chain.records, files: chain.files })} />
          ) : (
            <Alert tone="danger" title={t('audit.chainBroken', { file: chain.broken_at?.file, line: chain.broken_at?.line, reason: chain.broken_at?.reason })} />
          )}
        </div>
      ) : null}
      {verify.error ? <div className="mb-4"><ErrorState error={verify.error} /></div> : null}

      <Card className="mb-4">
        <CardContent>
          <form className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6" onSubmit={submit} role="search" aria-label={t('audit.filter')}>
            <Field id="filtro-de" label={t('audit.from')}>
              <Input type="date" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} />
            </Field>
            <Field id="filtro-ate" label={t('audit.to')}>
              <Input type="date" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
            </Field>
            <Field id="filtro-usuario" label={t('audit.user')}>
              <Input value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} />
            </Field>
            <Field id="filtro-ferramenta" label={t('audit.tool')}>
              <NativeSelect value={form.tool} onChange={(e) => setForm({ ...form, tool: e.target.value })}>
                <option value="">{t('audit.any')}</option>
                {ALL_TOOLS.map((x) => (
                  <option key={x} value={x}>
                    {t(`channels.toolNames.${x}`)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field id="filtro-estado" label={t('audit.status')}>
              <NativeSelect value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="">{t('audit.any')}</option>
                {(['ok', 'clarification', 'no_answer', 'error'] as const).map((s) => (
                  <option key={s} value={s}>
                    {t(`audit.statuses.${s}`)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field id="filtro-assunto" label={t('audit.subject')}>
              <NativeSelect value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })}>
                <option value="">{t('audit.any')}</option>
                {(subjects.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <div className="flex gap-2 sm:col-span-2 lg:col-span-6">
              <Button type="submit" variant="primary">
                {t('audit.filter')}
              </Button>
              <Button
                onClick={() => {
                  setForm(EMPTY);
                  setApplied(EMPTY);
                }}
              >
                {t('audit.clear')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {q.isPending ? (
        <LoadingBlock rows={8} />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState>{t('audit.empty')}</EmptyState>
      ) : (
        <div aria-busy={q.isPlaceholderData} className={cn('lr-fade contents', q.isPlaceholderData && 'lr-stale')}>
          <Table stickyHeader>
            <TableCaption>{t('audit.title')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{t('audit.when')}</TableHead>
                <TableHead>{t('audit.user')}</TableHead>
                <TableHead>{t('audit.tool')}</TableHead>
                <TableHead>{t('audit.subject')}</TableHead>
                <TableHead>{t('audit.query')}</TableHead>
                <TableHead>{t('audit.status')}</TableHead>
                <TableHead className="text-right">{t('audit.duration')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.correlation_id}-${r.ts}-${r.tool}`} className="hover:bg-sunken">
                  <TableCell className="tabular whitespace-nowrap">
                    <button type="button" className="text-left underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-focus" onClick={() => setDetail(r)}>
                      {formatDateTime(r.ts)}
                    </button>
                  </TableCell>
                  <TableCell>{r.user}</TableCell>
                  <TableCell>{r.tool ? t(`channels.toolNames.${r.tool}`) : <span className="text-fg-muted">{t('audit.beforeTool')}</span>}</TableCell>
                  <TableCell>{r.subject_id ?? '-'}</TableCell>
                  <TableCell className="font-mono text-[length:var(--lr-text-xs)]">{r.query_id ?? '-'}</TableCell>
                  <TableCell>
                    <StatusBadge tone={STATUS_TONE[r.status]}>{t(`audit.statuses.${r.status}`)}</StatusBadge>
                  </TableCell>
                  <TableCell className="tabular text-right">{formatNumber(r.duration_ms)} ms</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-[length:var(--lr-text-sm)] text-fg-secondary" aria-live="polite">
              {t('audit.shown', { count: rows.length })}
            </span>
            {q.hasNextPage ? (
              <Button onClick={() => void q.fetchNextPage()} disabled={q.isFetchingNextPage}>
                {t('audit.loadMore')}
              </Button>
            ) : null}
          </div>
        </div>
      )}
      {detail ? <Detail r={detail} onClose={() => setDetail(null)} /> : null}
    </AdminOnly>
  );
}
